import React from "react";
import { CRMIcon, TIcon } from "../../CRMIcon/CRMIcon";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { DateTime } from "luxon";
import { pipe } from "fp-ts/function";
import { ifTrueInject } from "../../../../../shared/src/utilsByDomain/array";
import { TAction } from "../../CRMTableRow/CRMTableRow";
import { isAFunctionCurried, isAPlainFunction, onCallIfExists } from "../../../functions/functions";
import { TSimpleLegalEmailWithPersonsForTriageForm } from "../../../../../domain/codecs/form/TriageForm";
import { CRMColors } from "../../../models/CRMColors";
import { Background } from "../../BuildingBlocks/Background";
import { Text } from "../../BuildingBlocks/Text";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { Padding } from "../../BuildingBlocks/Padding";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { useHover } from "../../../hooks/useHover";
import { CRMInputCheckboxComponent } from "../../CRMInputCheckboxComponent/CRMInputCheckboxComponent";
import { CRMTimelineControlsWrapper } from "../../CRMTimelineControlsWrapper/CRMTimelineControlsWrapper";
import { MaxWidth } from "../../BuildingBlocks/MaxWidth";
import { CRMEmailCardToFrom } from "../CRMEmailCardToFrom/CRMEmailCardToFrom";
import { CRMFuzzySortSearchHighlight } from "../../Simple/CRMFuzzySortSearchHighlight/CRMFuzzySortSearchHighlight";
import { CRMUserAvatar } from "../CRMUserAvatar/CRMUserAvatar";
import { TLegalEmailAssignee } from "../../../../../domain/codecs/LegalEmail";


export type TEmailCardProps = {
    email: TSimpleLegalEmailWithPersonsForTriageForm;
    isSelected?: boolean;
    showUnresolvedIndicator?: boolean;
    searchTerm: string;
    onClick: () => void;
    onSelect?: () => void;
    onAssign?: () => void;
    onAssignRelated?: () => void;
    onArchive?: () => void;
    onDisassociate?: () => void;
    onReply?: () => void;
    onForward?: () => void;
};

export const CRMEmailCard = (props: React.PropsWithChildren<TEmailCardProps>): JSX.Element => {

    const [ref, isHovered] = useHover<HTMLDivElement>({});

    const showUnresolved = (): boolean =>
        props.showUnresolvedIndicator === true
        && props.email.original.resolved_at === null
        && props.email.original.direction === "inbound";

    const getPrettySentAtDate = () => DateTime
        .fromISO(props.email.original.sent_at)
        .setZone("Europe/London")
        .toFormat("d LLL")
    ;

    const assignee = props.email.original.assignee ?? null;

    // Initials alone would read as "they are on it" - 78% of unresolved claims have lapsed.
    const getAssigneeTitle = (user: TLegalEmailAssignee): string => {
        const name = `${user.first_name} ${user.last_name}`.trim();

        if (user.assignment_expires_at === null) {
            return `${name} — in their inbox`;
        }

        const expires = DateTime.fromISO(user.assignment_expires_at).setZone("Europe/London");

        return expires < DateTime.local()
            ? `${name} — claim lapsed ${expires.toFormat("d LLL yyyy")}`
            : `${name} — claimed until ${expires.toFormat("d LLL")}`;
    };

    const getActions = () => {
        return pipe(
            [] as Array<TAction>,
            ifTrueInject(
                isAFunctionCurried(props.onAssign),
                {
                    iconName: "file-box" as TIcon,
                    title: "Assign",
                    onClick: onCallIfExists(props.onAssign),
                }
            ),
            ifTrueInject(
                isAFunctionCurried(props.onAssignRelated),
                {
                    iconName: "done" as TIcon,
                    title: "Assign",
                    onClick: onCallIfExists(props.onAssignRelated),
                }
            ),
            ifTrueInject(
                isAFunctionCurried(props.onArchive),
                {
                    iconName: "archive" as TIcon,
                    title: "Archive",
                    onClick: onCallIfExists(props.onArchive),
                }
            ),
            ifTrueInject(
                isAFunctionCurried(props.onDisassociate),
                {
                    iconName: "unfile" as TIcon,
                    title: "Disassociate",
                    onClick: onCallIfExists(props.onDisassociate),
                }
            ),
            ifTrueInject(
                isAFunctionCurried(props.onReply),
                {
                    iconName: "reply" as TIcon,
                    title: "Reply",
                    onClick: onCallIfExists(props.onReply),
                }
            ),
            ifTrueInject(
                isAFunctionCurried(props.onForward),
                {
                    iconName: "forward-arrow" as TIcon,
                    title: "Forward",
                    onClick: onCallIfExists(props.onForward),
                }
            ),
        )
    }

    return (
        <div className="crm-email-card" ref={ref}>
            <CRMTimelineControlsWrapper
                top="10px"
                right="0px"
                backgroundColour={CRMColors.NEUTRAL_PAPER}
            >
                {isHovered && getActions().map((action, index) => (
                    <div key={index}>
                        <CRMIcon
                            iconName={action.iconName}
                            colour="neutral-ink"
                            size="medium"
                            title={action.title}
                            cursor="pointer"
                            onClick={action.onClick}
                        />
                    </div>
                ))}

                {isAPlainFunction(props.onSelect) && 
                    <CRMInputCheckboxComponent
                        checked={props.isSelected || false}
                        cursor="pointer"
                        size="medium"
                        onClick={props.onSelect}
                    />
                }
            </CRMTimelineControlsWrapper>

            <Background
                color={CRMColors.NEUTRAL_PAPER}
                padding={`${CRMSpacing.MEDIUM} 0px`}
                onClick={props.onClick}
            >
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    {/* TITLE */}
                    <MaxWidth width="80%">
                        <Padding type="horizontal" spacing={CRMSpacing.MEDIUM}>
                            <Text 
                                size={CRMFontSizes.SMALL} 
                                lineHeight={1.2}
                                weight={700}
                            >
                                <CRMFuzzySortSearchHighlight
                                    text={props.email.original.subject}
                                    searchText={props.searchTerm}
                                />
                            </Text>
                        </Padding>
                    </MaxWidth>
                    
                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        {/* FROM - TO STRIP */}
                        <CRMEmailCardToFrom 
                            email={props.email}
                            searchTerm={props.searchTerm}
                        />
                        
                        {/* SNIPPET */}
                        <Padding type="horizontal" spacing={CRMSpacing.MEDIUM}>
                            <Text 
                                size={CRMFontSizes.SMALL} 
                                lineHeight={1.4}
                                weight={400}
                            >
                                <CRMFuzzySortSearchHighlight
                                    text={props.email.original.snippet}
                                    searchText={props.searchTerm}
                                />
                            </Text>
                        </Padding>
                        
                        {/* SENT AT */}
                        <SpacingRow justifyContent="end" spacing={CRMSpacing.TINY}>
                            {showUnresolved() && assignee !== null &&
                                <div title={getAssigneeTitle(assignee)}>
                                    <CRMUserAvatar
                                        user={assignee}
                                        size="tiny"
                                    />
                                </div>
                            }
                            {showUnresolved() &&
                                <CRMIcon
                                    iconName="email-pending"
                                    size="small"
                                    colour="highlights-pertinent-yellow-0"
                                    title="Unresolved — someone still owes a reply"
                                />
                            }
                            <Padding type="horizontal" spacing={CRMSpacing.MEDIUM}>
                                <Text 
                                    size={CRMFontSizes.SMALL} 
                                    lineHeight={1.4}
                                    weight={400}
                                    align="right"
                                >
                                    {getPrettySentAtDate()}
                                </Text>
                            </Padding>
                        </SpacingRow>
                    </SpacingColumn>

                </SpacingColumn>
            </Background>
        </div>
    );
};
